import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { LazyMountIframe, useLazyMountIframe } from '@/components/tiptap-editor/tiptap-extension/lazy-mount-tool-iframe';
import { Switch } from '@/components/ui/switch';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useNodeViewCollaborationPresence } from '@/hooks/use-node-view-collaboration-presence';
import type { AttributeDef } from '@/lib/parseZyXML';
import { parseZyXML } from '@/lib/parseZyXML';
import { DEFAULT_EDITOR_OPTIONS } from '@/lib/utils';
import { Editor as MonacoEditor } from '@monaco-editor/react';
import { type ReactNodeViewProps } from '@tiptap/react';
import { connectToChild } from 'penpal';
import { memo, useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

const CodeWritingBuilderNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, selected, editor, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { id, codeWritingXMLToLoad } = node.attrs;
    const url = `${import.meta.env.VITE_ZYBOOKS_URL}/modern-authoring-tools/code-writing-builder`;

    const [isDevMode, setIsDevMode] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const exitFullscreenRef = useRef<(() => void) | undefined>(undefined);
    const { shouldMount, isIframeLoaded, handleIframeLoad, resetLoadState } = useLazyMountIframe({
        containerRef,
        editor,
        forceMount: selected,
        id,
    });
    const { presenceUsers, presenceTracking } = useNodeViewCollaborationPresence(editor, id, 'codeWritingBuilder', selected);

    const parseZyXMLConfig = {
        rootTag: 'zyTool',
        xmlPropName: 'codeWritingXMLToLoad',
        attributes: [
            { name: 'caption', type: 'string' },
            { name: 'challenge', type: 'boolean' },
            { name: 'parts', type: 'number' },
        ] as AttributeDef[],
        hasOptions: true,
    };

    const handleUpdateCodeWritingXML = useDebounceCallback((codeWritingXMLToLoad: string) => {
        const updates = parseZyXML(codeWritingXMLToLoad, parseZyXMLConfig);
        if (updates) updateAttributes(updates);
    }, 300);

    useEffect(() => {
        if (!shouldMount || isDevMode || !iframeRef.current) {
            return;
        }

        const connection = connectToChild({
            iframe: iframeRef.current,
            methods: {
                updateParentXML: (XML: string) => {
                    handleUpdateCodeWritingXML(XML);
                },
                getParentXML: () => {
                    return codeWritingXMLToLoad;
                },
                getBaseImageUrl: () => {
                    return editor.state.doc.attrs.baseImageUrl;
                },
                notifyEscapeKeyPressed: () => {
                    exitFullscreenRef.current?.();
                },
            },
        });

        return () => connection.destroy();
    }, [shouldMount, isDevMode]); // eslint-disable-line react-hooks/exhaustive-deps -- We don't want to re-run this effect when codeWritingXMLToLoad changes

    const handleDevModeChange = (checked: boolean) => {
        resetLoadState();
        setIsDevMode(checked);
    };

    const mainContent = isDevMode ? (
        <MonacoEditor
            defaultLanguage="xml"
            value={codeWritingXMLToLoad}
            onChange={value => handleUpdateCodeWritingXML(value || '')}
            options={{ ...DEFAULT_EDITOR_OPTIONS, readOnly: isEditorReadonly }}
            theme="vs"
        />
    ) : (
        <LazyMountIframe
            containerRef={containerRef}
            shouldMount={shouldMount}
            id={id}
            url={url}
            title="Code Writing"
            iframeRef={iframeRef}
            loadingMessage="Loading code writing…"
            isIframeLoaded={isIframeLoaded}
            onLoad={handleIframeLoad}
            iframeClassName="h-full w-full"
        />
    );

    const settingsPanel = (
        <div className="bg-gray-100 px-4 py-2 pe-10 dark:bg-gray-800 flex items-center justify-end border-b gap-x-2">
            <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400">
                Dev mode:
                <Switch
                    checked={isDevMode}
                    disabled={isEditorReadonly}
                    onCheckedChange={handleDevModeChange}
                />
            </label>
        </div>
    );

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label="Code Writing"
            selected={selected}
            contentClassName="mx-0"
            settingsPanel={settingsPanel}
            presenceTracking={presenceTracking}
            presenceUsers={presenceUsers}
            fullscreenContent={mainContent}
            exitFullscreenRef={exitFullscreenRef}
            fullscreenMode="inline"
        >
            {mainContent}
        </CommonNodeViewWrapper>
    );
};

export default memo(CodeWritingBuilderNodeView);
